import { push } from 'connected-react-router';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { useDispatch } from 'react-redux';
import { PatientRoutes } from 'routes';
import { QuestionnaireIntro, QuestionnaireProps } from './questionnaire-intro';

import DizzinessLogo from 'img/dhi-questionnaire.svg';

import { SignOutButton } from 'components/button/button-sign-out';
import PcsLogo from 'img/pcs-questionnaire.svg';
import PromisLogo from 'img/promis-questionnaire.svg';
import CgsLogo from 'img/symptoms-questionnaire.svg';
import { QuestionnaireDataProps } from '../types';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';

type QuestionnaireDataPropsFactory = (args: {
  assessmentType: AssessmentType;
}) => QuestionnaireDataProps;

// promis A
export const promisDataA: QuestionnaireDataPropsFactory = props => ({
  imgLogo: PromisLogo,
  header: {
    title: '1st Questionnaire',
    headerButtonOverride: () => <SignOutButton rounded />
  },
  subtitle: '1st questionnaire'.toUpperCase(),
  title:
    props.assessmentType === 'post-injury'
      ? 'Assessing your function'
      : 'Understanding your typical function',
  description:
    props.assessmentType === 'post-injury'
      ? `This questionnaire is to identify difficulties that you may be experiencing in a number of functional areas. You'll have an option for a break halfway, but we recommend you finish in one go.`
      : `This questionnaire helps establish how you usually function across several key areas. Your responses create a personalized baseline that clinicians can use for comparison if an injury occurs. You can take a short break halfway, but we recommend completing it in one sitting for accuracy.`,
  duration: '5 mins'
});

// cgs
export const cgsData: QuestionnaireDataPropsFactory = props => ({
  imgLogo: CgsLogo,
  header: { title: '2nd Questionnaire' },
  subtitle: '2nd questionnaire'.toUpperCase(),
  title:
    props.assessmentType === 'post-injury'
      ? 'Evaluate your symptoms'
      : 'Understanding how you typically feel',
  description:
    props.assessmentType === 'post-injury'
      ? `This questionnaire is to evaluate your concussion symptoms. After reading each symptom, please choose the number that best describes the way you have been feeling today.`
      : `This questionnaire helps document how you normally feel on a day-to-day basis. Please select the number that describes the way you have been feeling today.`,
  duration: '3 mins'
});

// pcs post concussion syndrome
export const pcsData: QuestionnaireDataPropsFactory = () => ({
  imgLogo: PcsLogo,
  header: { title: '3rd Questionnaire' },
  subtitle: '3rd questionnaire'.toUpperCase(),
  title: 'Understanding your pain',
  description:
    'This questionnaire is to understand how you are feeling when in pain. Using the following scale, please indicate the degree to which you have these thoughts and feelings when you are experiencing pain.',
  duration: '3 mins'
});

// dhi
export const dhiData: QuestionnaireDataPropsFactory = () => ({
  imgLogo: DizzinessLogo,
  header: { title: '4th Questionnaire' },
  subtitle: '4th questionnaire'.toUpperCase(),
  title: 'Assessing your dizziness',
  description:
    'The purpose of this questionnaire is to identify difficulties that you may be experiencing because of dizziness. Answer each question only as it pertains to dizziness.',
  duration: '5 mins'
});

export interface QuestionnaireIntroWrapperProps
  extends Omit<QuestionnaireProps, 'data'> {
  patientId: string;
  assessmentType: AssessmentType;
  data?: QuestionnaireProps['data'];
}

export function IntroPromisA(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();

  return (
    <QuestionnaireIntro
      data={promisDataA(props)}
      startQuestionnaire={() => {
        dispatch(
          push(getPatientLinkUrl(PatientRoutes.PatientPromis, props.patientId))
        );
      }}
    />
  );
}

export function IntroInjury(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();
  return (
    <QuestionnaireIntro
      data={{
        duration: '1 min',
        header: { title: 'Injury' },
        imgLogo: '',
        title: 'Injury Data',
        subtitle: '',
        description: 'Help us understand your injury'
      }}
      startQuestionnaire={() => {
        dispatch(
          push(
            getPatientLinkUrl(
              PatientRoutes.QuestionnaireInjury,
              props.patientId
            )
          )
        );
      }}
    />
  );
}

export function IntroDemographic(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();
  return (
    <QuestionnaireIntro
      data={{
        duration: '1 min',
        header: { title: 'Demographic Data' },
        imgLogo: '',
        title: 'Demographic Data',
        subtitle: '',
        description: 'This information helps us learn more about you'
      }}
      startQuestionnaire={() => {
        dispatch(
          push(
            getPatientLinkUrl(
              PatientRoutes.QuestionnaireDemographics,
              props.patientId
            )
          )
        );
      }}
    />
  );
}

export function IntroCgs(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();
  return (
    <QuestionnaireIntro
      data={cgsData(props)}
      startQuestionnaire={() => {
        dispatch(
          push(getPatientLinkUrl(PatientRoutes.PatientCgs, props.patientId))
        );
      }}
    />
  );
}

export function IntroPcs(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();
  return (
    <QuestionnaireIntro
      data={pcsData(props)}
      startQuestionnaire={() => {
        dispatch(
          push(getPatientLinkUrl(PatientRoutes.PatientPcs, props.patientId))
        );
      }}
    />
  );
}
export function IntroDhi(props: QuestionnaireIntroWrapperProps) {
  const dispatch = useDispatch();
  return (
    <QuestionnaireIntro
      data={dhiData(props)}
      startQuestionnaire={() => {
        dispatch(
          push(getPatientLinkUrl(PatientRoutes.PatientDhi, props.patientId))
        );
      }}
    />
  );
}
